/**
 * Bar-specific data preparation.
 *
 * The shared cartesian normalizer does the work both cartesian charts need
 * (see ../cartesianUtils). What is added here is everything that follows from
 * drawing a length rather than a path:
 *
 * - a bar's meaning is its length from zero, so the category list has to be
 *   capped rather than crammed: past a few dozen bars each one is a sliver
 * - ranking is the common bar question, so the sort understands value order as
 *   well as category order
 * - a stack of mixed signs is not a stack, it is two charts drawn on top of
 *   each other, so it is caught and reported
 */

import {
  hasMixedSigns,
  normalizeSeriesData,
  type CartesianPoint,
  type ChartSeries,
  type SeriesInput,
} from "../cartesianUtils";

export type { ChartSeries, SeriesInput };
/** Sign checks are shared with the area chart, which stacks under the same rule. */
export { hasMixedSigns, hasNegativeValues } from "../cartesianUtils";
/** A bar's point is the shared cartesian point under the bar chart's name. */
export type BarPoint = CartesianPoint;

export interface BarHighlight {
  stroke?: string;
}

/**
 * Category order. "asc" and "desc" order by the category label, "valueAsc" and
 * "valueDesc" by each category's total across every series, which is the
 * ranking a bar chart is usually asked for.
 */
export type BarSort = "none" | "asc" | "desc" | "valueAsc" | "valueDesc";

export interface NormalizedBarData {
  points: BarPoint[];
  series: ChartSeries[];
  warnings: string[];
}

export interface NormalizeBarOptions {
  categoryKey: string;
  series?: SeriesInput[];
  palette: readonly string[];
  maxSeries: number;
  /** Categories past this cap are dropped and counted in a warning. */
  maxCategories: number;
  sort: BarSort;
  /** Stacked charts add checks that a grouped chart does not need. */
  stacked: boolean;
}

/** Sum of every series at one category, treating missing values as absent. */
function pointTotal(point: BarPoint, series: ChartSeries[]): number {
  return series.reduce((sum, s) => {
    const value = point[s.key];
    return typeof value === "number" ? sum + value : sum;
  }, 0);
}

/**
 * Order the categories.
 *
 * Value sorting uses the total across all series, not the visible ones, so
 * hiding a series from the legend never reshuffles the bars underneath the
 * reader's cursor.
 */
export function sortBarPoints(
  points: BarPoint[],
  series: ChartSeries[],
  sort: BarSort,
): BarPoint[] {
  if (sort === "none") return points;
  const sorted = [...points];

  if (sort === "valueAsc" || sort === "valueDesc") {
    sorted.sort((a, b) => {
      const cmp = pointTotal(a, series) - pointTotal(b, series);
      return sort === "valueAsc" ? cmp : -cmp;
    });
    return sorted;
  }

  sorted.sort((a, b) => {
    const na = Number(a.x);
    const nb = Number(b.x);
    const both = Number.isFinite(na) && Number.isFinite(nb);
    const cmp = both ? na - nb : String(a.x).localeCompare(String(b.x));
    return sort === "asc" ? cmp : -cmp;
  });
  return sorted;
}

/** Longest category label, used to size the label gutter on horizontal bars. */
export function longestLabelLength(points: BarPoint[]): number {
  return points.reduce((longest, point) => Math.max(longest, String(point.x ?? "").length), 0);
}

/**
 * Prepare rows for the bar chart: normalize, order, cap, and report.
 *
 * Returns points and series that are always safe to render, plus the notes a
 * reader needs to trust what they are looking at.
 */
export function normalizeBarData(
  data: unknown,
  options: NormalizeBarOptions,
): NormalizedBarData {
  const { categoryKey, series: seriesInput, palette, maxSeries, maxCategories, sort, stacked } =
    options;

  const normalized = normalizeSeriesData(data, {
    xKey: categoryKey,
    series: seriesInput,
    palette,
    maxSeries,
    // Ordering happens below, where value sorting is also available.
    sort: "none",
    missingNote: "left without a bar",
  });

  const warnings = [...normalized.warnings];
  const { series } = normalized;
  let points = sortBarPoints(normalized.points, series, sort);

  // A bar has to be wide enough to compare. Past the cap the extras are dropped
  // rather than shaved into slivers, and the reader is told how many went.
  const cap = Math.max(1, Math.floor(maxCategories));
  if (points.length > cap) {
    const dropped = points.length - cap;
    points = points.slice(0, cap);
    warnings.push(
      `Showing ${cap} of ${cap + dropped} categories. ${dropped} more ${dropped === 1 ? "is" : "are"} not charted; sort by value or filter the data to choose which.`,
    );
  }

  if (stacked && series.length > 1 && hasMixedSigns(points, series)) {
    warnings.push(
      "This data mixes positive and negative values, which a stack cannot add up. Grouped bars show it honestly.",
    );
  }

  return { points, series, warnings };
}
